<template>
  <div class="ma-page">
    <div class="announcement">
      <span>Découvrez la nouvelle expérience Minds' Academy</span>
      <router-link :to="{ name: 'minds-register' }">
        Explorer la plateforme <span aria-hidden="true">→</span>
      </router-link>
    </div>

    <div class="contact-strip">
      <span
        class="contact-icon"
        aria-hidden="true"
      >◔</span>
      <strong>Parlez à notre équipe</strong>
      <router-link :to="{ name: 'contact' }">
        Réserver un échange
      </router-link>
    </div>

    <header class="site-header">
      <router-link
        :to="{ name: 'home' }"
        class="brand"
        aria-label="Minds' Academy — Accueil"
      >
        <img
          src="/minds-academy-lockup.png"
          alt="Minds' Academy"
        >
      </router-link>

      <nav
        class="desktop-nav"
        aria-label="Navigation principale"
      >
        <router-link :to="{ name: 'videos' }">
          Nos vidéos
        </router-link>
        <router-link :to="{ name: 'phototheque' }">
          Photothèque
        </router-link>
        <a href="#temoignage">Pourquoi Minds' Academy</a>
        <a href="#temoignages-etudiants">Témoignages</a>
      </nav>

      <div class="header-actions">
        <router-link
          :to="{ name: 'login-student' }"
          class="login-link"
        >
          Se connecter
        </router-link>
        <router-link
          :to="{ name: 'minds-register' }"
          class="header-signup"
        >
          S'inscrire
        </router-link>
      </div>

      <button
        type="button"
        class="menu-toggle"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        aria-label="Ouvrir le menu"
        @click="menuOpen = !menuOpen"
      >
        <span />
        <span />
        <span />
      </button>

      <div
        v-if="menuOpen"
        class="mobile-nav"
      >
        <router-link
          :to="{ name: 'videos' }"
          @click.native="menuOpen = false"
        >
          Nos vidéos
        </router-link>
        <router-link
          :to="{ name: 'phototheque' }"
          @click.native="menuOpen = false"
        >
          Photothèque
        </router-link>
        <a
          href="#temoignage"
          @click="menuOpen = false"
        >Pourquoi Minds' Academy</a>
        <a
          href="#temoignages-etudiants"
          @click="menuOpen = false"
        >Témoignages</a>
        <router-link :to="{ name: 'login-student' }">
          Se connecter
        </router-link>
        <router-link
          :to="{ name: 'minds-register' }"
          class="mobile-signup"
        >
          S'inscrire
        </router-link>
      </div>
    </header>

    <main>
      <section class="hero">
        <div class="hero-copy">
          <span class="eyebrow">La plateforme éducative tout-en-un</span>
          <h1>
            Passez au niveau supérieur avec
            <em>l'apprentissage intelligent.</em>
          </h1>
          <p>
            Minds' Academy réunit étudiants, enseignants et établissements
            dans une expérience conçue pour mieux apprendre, enseigner et piloter.
          </p>
          <div class="hero-actions">
            <router-link
              :to="{ name: 'contact' }"
              class="button button-primary"
            >
              Je discute avec un conseiller
            </router-link>
            <router-link
              :to="{ name: 'videos' }"
              class="button button-outline"
            >
              Découvrir nos vidéos <span aria-hidden="true">→</span>
            </router-link>
          </div>
          <div class="availability">
            <span>▱ <strong>Disponible en ligne</strong></span>
            <span>◷ <strong>Un accompagnement à chaque étape</strong></span>
          </div>
        </div>

        <div
          class="hero-art"
          aria-hidden="true"
        >
          <div class="hero-orbit orbit-one" />
          <div class="hero-orbit orbit-two" />
          <div class="hero-mark">
            <img
              src="/team-avatar.jpg"
              alt=""
            >
          </div>
          <div class="floating-card card-student">
            <span>01</span>
            <strong>Étudiants</strong>
            <small>Apprendre à son rythme</small>
          </div>
          <div class="floating-card card-teacher">
            <span>02</span>
            <strong>Enseignants</strong>
            <small>Enseigner plus sereinement</small>
          </div>
          <div class="floating-card card-school">
            <span>03</span>
            <strong>Établissements</strong>
            <small>Piloter avec clarté</small>
          </div>
        </div>
      </section>

      <section
        id="videos"
        class="yt-section"
      >
        <div class="section-heading">
          <span>Notre chaîne YouTube</span>
          <h2>Apprenez gratuitement avec nos vidéos.</h2>
          <p>
            Des cours complets et des exercices corrigés, disponibles à tout moment
            sur la chaîne Minds' Academy.
          </p>
        </div>

        <div class="videos-grid">
          <article
            v-for="video in videos.slice(0, 3)"
            :key="video.id"
            class="video-card"
          >
            <a
              :href="`https://www.youtube.com/watch?v=${video.id}`"
              target="_blank"
              rel="noopener"
              class="video-thumb"
            >
              <img
                :src="`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`"
                :alt="video.title"
                loading="lazy"
                @error="$event.target.src = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`"
              >
              <span class="video-duration">{{ video.duration }}</span>
            </a>
            <span
              class="video-tag"
              :class="video.tone"
            >{{ video.tag }}</span>
            <h3>{{ video.title }}</h3>
            <p>{{ video.description }}</p>
            <a
              :href="`https://www.youtube.com/watch?v=${video.id}`"
              target="_blank"
              rel="noopener"
              class="video-button"
            >
              Regarder la vidéo
            </a>
          </article>
        </div>

        <router-link
          :to="{ name: 'videos' }"
          class="button channel-button"
        >
          Voir toutes les vidéos <span aria-hidden="true">→</span>
        </router-link>
      </section>

      <section
        id="temoignage"
        class="vision"
      >
        <div class="vision-photo">
          <img
            src="/monaco-tuttop.jpg"
            alt="Le fondateur de Minds' Academy avec M. Christophe Mirmand, Ministre d'État de Monaco, dans les locaux de TUT'TOP"
          >
        </div>
        <div class="vision-copy">
          <span class="eyebrow">Échelle internationale</span>
          <blockquote>
            « Une expertise edtech reconnue à l'échelle internationale,
            de la Tunisie à Monaco. »
          </blockquote>
          <p>
            Notre fondateur dirige également TUT'TOP, sa startup edtech basée
            à Monaco — ici lors de la visite de M. Christophe Mirmand,
            Ministre d'État de Monaco. Cette dimension internationale nourrit
            chaque jour la qualité de Minds' Academy.
          </p>
          <strong>TUT'TOP — Monaco × Minds' Academy</strong>
        </div>
      </section>

      <section
        id="temoignages-etudiants"
        class="testimonials"
      >
        <div class="section-heading">
          <span>Témoignages étudiants</span>
          <h2>Ils ont vécu l'expérience Minds' Academy.</h2>
          <p>
            Découvrez ce que nos étudiants pensent de leur parcours
            et de l'accompagnement qu'ils ont reçu.
          </p>
        </div>
        <div class="testimonial-grid">
          <div
            v-for="video in testimonialVideos"
            :key="video.id"
            class="testimonial-card"
          >
            <div
              class="testimonial-thumb"
              @click="playTestimonial(video)"
            >
              <img
                :src="video.playing ? '' : `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`"
                :alt="video.name"
                loading="lazy"
              >
              <iframe
                v-if="video.playing"
                :src="`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`"
                frameborder="0"
                allow="autoplay; encrypted-media"
                allowfullscreen
              />
              <span
                v-if="!video.playing"
                class="play-btn"
              >▶</span>
              <span
                v-if="!video.playing"
                class="duration"
              >{{ video.duration }}</span>
            </div>
            <h3>{{ video.name }}</h3>
            <p>{{ video.label }}</p>
          </div>
        </div>
      </section>

    </main>

    <footer class="site-footer">
      <div class="footer-main">
        <div class="footer-brand">
          <div class="footer-logo">
            <img
              src="/minds-academy-mark.png"
              alt="Minds' Academy"
            >
            <span>Minds' Academy</span>
          </div>
          <p>
            La plateforme qui réunit apprentissage, accompagnement
            et gestion pédagogique.
          </p>
          <router-link
            :to="{ name: 'contact' }"
            class="footer-contact"
          >
            Nous contacter
          </router-link>
          <div class="footer-social">
            <a
              href="https://www.facebook.com/Mindsacademy13"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >Facebook</a>
            <a
              href="https://www.instagram.com/mindsacademy13/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >Instagram</a>
            <a
              href="https://wa.me/21695624160"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >WhatsApp</a>
          </div>
        </div>
        <div>
          <h3>Plateforme</h3>
          <router-link :to="{ name: 'videos' }">
            Nos vidéos
          </router-link>
          <router-link :to="{ name: 'phototheque' }">
            Photothèque
          </router-link>
        </div>
        <div>
          <h3>Accès</h3>
          <router-link :to="{ name: 'login-student' }">
            Espace étudiant
          </router-link>
          <router-link :to="{ name: 'login-tutor' }">
            Espace enseignant
          </router-link>
          <router-link :to="{ name: 'minds-register' }">
            Créer un compte
          </router-link>
        </div>
        <div>
          <h3>Légal</h3>
          <router-link :to="{ name: 'privacy-policy' }">
            Confidentialité
          </router-link>
          <router-link :to="{ name: 'terms-of-use' }">
            Conditions d'utilisation
          </router-link>
        </div>
      </div>
      <div class="footer-bottom">
        <span>© 2026 Minds' Academy. Tous droits réservés.</span>
        <span>www.mindsacademy.io</span>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  data() {
    return {
      menuOpen: false,
      videos: [
        {
          id: 'R4UMlmCbON4',
          tag: 'Logique',
          tone: 'tone-teal',
          duration: '11:38',
          title: 'Logique mathématique : Partie 1',
          description: 'Les propositions et les bases de la logique mathématique, expliquées simplement pour bien démarrer.',
        },
        {
          id: 'LxT-SlQKCw4',
          tag: 'Logique',
          tone: 'tone-violet',
          duration: '19:51',
          title: 'Les connecteurs logiques',
          description: 'Conjonction, disjonction, implication : maîtrisez les connecteurs logiques avec des exemples corrigés.',
        },
        {
          id: '9u0f8dnul7Y',
          tag: 'Analyse numérique',
          tone: 'tone-coral',
          duration: '12:16',
          title: 'Équations non linéaires : la dichotomie',
          description: 'Résolvez les équations non linéaires pas à pas avec la méthode de la dichotomie.',
        },
        {
          id: 'q7NqmyYyTHM',
          tag: 'Analyse',
          tone: 'tone-coral',
          duration: '22:27',
          title: "Équations différentielles d'ordre 1",
          description: 'La méthode complète pour résoudre les équations différentielles linéaires du premier ordre.',
        },
        {
          id: 'nt05Zq--WRg',
          tag: 'SGBD',
          tone: 'tone-teal',
          duration: '22:13',
          title: 'SGBD : curseurs et exceptions',
          description: 'Maîtrisez les curseurs PL/SQL et la gestion des exceptions avec des exemples pratiques.',
        },
        {
          id: 'xoGCLTcK8M4',
          tag: 'SGBD',
          tone: 'tone-violet',
          duration: '36:28',
          title: 'SGBD : les objets (vue, séquence, index)',
          description: 'Vues, synonymes, séquences et index : tous les objets de la base de données expliqués au tableau.',
        },
      ],
      testimonialVideos: [
        { id: 'YxWYsapOlq8', name: 'Zeineb', label: 'Étudiante — Promotion 2022', duration: '1:16', playing: false },
        { id: 'bXoahKg2QtY', name: 'Oussema', label: 'Étudiant — Promotion 2022', duration: '0:54', playing: false },
        { id: 'XjFlrmoWV0c', name: 'Student Spotlight', label: 'Étudiante — Promotion 2022', duration: '0:52', playing: false },
      ],
    }
  },
  methods: {
    playTestimonial(video) {
      video.playing = true
    },
  },
  mounted() {
    document.title = "Minds' Academy — Apprendre, enseigner et piloter"
  },
}
</script>

<style scoped>
.ma-page {
  --cream: #fffaf3;
  --ink: #100707;
  --teal: #18b9a7;
  --teal-dark: #0d756c;
  --coral: #ff6f59;
  --violet: #602bd9;
  min-height: 100vh;
  overflow-x: hidden;
  background: var(--cream);
  color: var(--ink);
  font-family: Inter, "Helvetica Neue", Arial, sans-serif;
}

.ma-page *,
.ma-page *::before,
.ma-page *::after {
  box-sizing: border-box;
}

.ma-page a {
  color: inherit;
  text-decoration: none;
}

.ma-page h1,
.ma-page h2,
.ma-page h3,
.ma-page h4,
.ma-page h5,
.ma-page h6 {
  color: inherit;
  font-family: inherit;
}

.announcement {
  min-height: 46px;
  padding: 10px 5vw;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  background: var(--violet);
  color: #fff;
  font-size: 15px;
}

.announcement a {
  color: #ff9b8d;
  font-weight: 800;
  text-decoration: underline;
}

.contact-strip {
  min-height: 46px;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #111;
  color: #fff;
}

.contact-strip a {
  color: #baf1e9;
}

.contact-icon {
  font-size: 24px;
  transform: rotate(-25deg);
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 60;
  min-height: 88px;
  padding: 12px clamp(24px, 5vw, 82px);
  display: flex;
  align-items: center;
  gap: 34px;
  background: rgba(255, 250, 243, 0.96);
  border-bottom: 1px solid rgba(16, 7, 7, 0.09);
  backdrop-filter: blur(16px);
}

.brand {
  width: 180px;
  height: 58px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.brand img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: clamp(18px, 2vw, 36px);
  font-size: 14px;
  font-weight: 650;
}

.desktop-nav a,
.login-link {
  position: relative;
}

.desktop-nav a::after,
.login-link::after {
  position: absolute;
  right: 0;
  bottom: -6px;
  left: 0;
  height: 2px;
  background: var(--coral);
  content: "";
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.22s ease;
}

.desktop-nav a:hover::after,
.login-link:hover::after {
  transform: scaleX(1);
}

.header-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 22px;
  font-size: 14px;
  font-weight: 750;
}

.header-cta,
.mobile-cta {
  padding: 13px 20px;
  background: var(--ink);
  color: #fff !important;
  border: 1px solid var(--ink);
  transition: 0.2s ease;
}

.header-cta:hover {
  background: var(--coral);
  border-color: var(--coral);
}

.header-signup,
.mobile-signup {
  padding: 13px 20px;
  border-radius: 999px;
  background: var(--coral);
  color: #fff !important;
  border: 1px solid var(--coral);
  font-weight: 800;
  transition: 0.2s ease;
}

.header-signup:hover,
.mobile-signup:hover {
  filter: brightness(0.94);
  box-shadow: 0 8px 22px rgba(255, 111, 89, 0.32);
}

.header-signup::after {
  display: none;
}

.menu-toggle,
.mobile-nav {
  display: none;
}

.hero {
  min-height: 730px;
  padding: clamp(64px, 9vw, 130px) clamp(24px, 7vw, 110px);
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(380px, 0.92fr);
  align-items: center;
  gap: 50px;
}

.eyebrow,
.section-heading > span {
  display: inline-flex;
  margin-bottom: 24px;
  padding: 8px 12px;
  border: 1px solid var(--ink);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.hero h1 {
  max-width: 850px;
  margin: 0;
  font-size: clamp(54px, 6vw, 96px);
  font-weight: 800;
  letter-spacing: -0.065em;
  line-height: 0.94;
}

.hero h1 em {
  display: block;
  color: var(--teal);
  font-style: normal;
}

.hero-copy > p {
  max-width: 720px;
  margin: 34px 0 0;
  font-size: clamp(18px, 1.6vw, 23px);
  line-height: 1.48;
}

.hero-actions {
  margin-top: 38px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.button {
  min-height: 54px;
  padding: 16px 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border: 1.5px solid var(--ink);
  font-size: 15px;
  font-weight: 800;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.button:hover {
  color: var(--ink);
  transform: translateY(-3px);
  box-shadow: 5px 5px 0 var(--ink);
}

.button-primary {
  min-width: 280px;
  background: var(--teal);
}

.button-outline {
  min-width: 280px;
  background: transparent;
}

.availability {
  margin-top: 34px;
  display: flex;
  flex-wrap: wrap;
  gap: 26px;
  font-size: 14px;
}

.availability span {
  display: flex;
  align-items: center;
  gap: 9px;
}

.hero-art {
  position: relative;
  min-height: 570px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 42%, rgba(24, 185, 167, 0.34), transparent 38%),
    linear-gradient(140deg, #efdbff 0%, #ffd7d0 55%, #fff1c7 100%);
  border-radius: 50% 50% 46% 54% / 42% 38% 62% 58%;
}

.hero-mark {
  position: absolute;
  z-index: 2;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
}

.hero-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.hero-orbit {
  position: absolute;
  border: 1px solid rgba(16, 7, 7, 0.22);
  border-radius: 50%;
}

.orbit-one {
  width: 88%;
  height: 68%;
  transform: rotate(24deg);
}

.orbit-two {
  width: 55%;
  height: 92%;
  transform: rotate(-38deg);
}

.floating-card {
  position: absolute;
  z-index: 3;
  min-width: 190px;
  padding: 18px;
  display: grid;
  background: #fff;
  border: 1.5px solid var(--ink);
  box-shadow: 7px 7px 0 var(--ink);
}

.floating-card span {
  color: var(--coral);
  font-size: 12px;
  font-weight: 850;
}

.floating-card strong {
  margin-top: 7px;
  font-size: 18px;
}

.floating-card small {
  margin-top: 3px;
  font-size: 12px;
}

.card-student {
  top: 13%;
  left: -4%;
}

.card-teacher {
  bottom: 2%;
  left: -2%;
}

.card-school {
  right: -3%;
  bottom: 48%;
}

.yt-section {
  padding: 95px clamp(24px, 8vw, 130px) 105px;
  background: #fff;
  text-align: center;
}

.videos-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 26px;
  text-align: left;
}

.video-card {
  display: flex;
  flex-direction: column;
  padding: 16px 16px 20px;
  background: #fff;
  border: 1px solid rgba(16, 7, 7, 0.13);
  border-radius: 14px;
  box-shadow: 0 10px 26px rgba(16, 7, 7, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 36px rgba(16, 7, 7, 0.1);
}

.video-thumb {
  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  background: #ece7df;
}

.video-thumb img {
  position: absolute;
  left: 0;
  top: -3%;
  width: 100%;
  height: 106%;
  display: block;
  object-fit: cover;
  object-position: center 22%;
  transition: transform 0.3s ease;
}

.video-card:hover .video-thumb img {
  transform: scale(1.045);
}

.video-duration {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 3px 8px;
  background: rgba(0, 0, 0, 0.82);
  color: #fff;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 750;
}

.video-tag {
  align-self: flex-start;
  margin-top: 17px;
  padding: 5px 12px;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.video-tag.tone-teal {
  background: var(--teal-dark);
}

.video-tag.tone-coral {
  background: var(--coral);
}

.video-tag.tone-violet {
  background: #8847f3;
}

.video-card h3 {
  margin: 13px 0 0;
  font-size: 20px;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.video-card p {
  flex: 1;
  margin: 10px 0 18px;
  color: #4c4646;
  font-size: 15px;
  line-height: 1.5;
}

.video-button {
  display: block;
  padding: 13px;
  background: var(--ink);
  border-radius: 8px;
  color: #fff !important;
  font-size: 14.5px;
  font-weight: 750;
  text-align: center;
  text-decoration: none;
  transition: background 0.2s ease;
}

.video-button:hover {
  background: var(--teal-dark);
}

.channel-button {
  margin-top: 46px;
  background: var(--ink);
  color: #fff !important;
}

.channel-button:hover {
  background: var(--teal-dark);
}

.section-heading {
  max-width: 850px;
  margin: 0 auto 52px;
  text-align: center;
}

.section-heading h2 {
  margin: 0;
  font-size: clamp(42px, 5vw, 72px);
  letter-spacing: -0.055em;
  line-height: 0.98;
}

.section-heading p {
  max-width: 680px;
  margin: 22px auto 0;
  font-size: 18px;
  line-height: 1.5;
}

.vision {
  min-height: 600px;
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  background: var(--cream);
}

.vision-photo {
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  background: var(--cream);
}

.vision-photo img {
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
}

.vision-copy {
  padding: clamp(60px, 8vw, 120px);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.vision-copy blockquote {
  margin: 0;
  font-size: clamp(28px, 2.9vw, 42px);
  font-weight: 750;
  letter-spacing: -0.045em;
  line-height: 1.12;
}

.vision-copy p {
  max-width: 620px;
  margin: 32px 0 22px;
  font-size: 18px;
  line-height: 1.5;
}

.vision-copy > strong {
  color: var(--teal-dark);
}

.testimonials {
  padding: 100px clamp(24px, 8vw, 130px);
  background: #fff;
}

.testimonial-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.testimonial-card {
  text-align: left;
}

.testimonial-thumb {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 16 / 9;
  background: #1a1a1a;
}

.testimonial-thumb img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.testimonial-thumb:hover img {
  transform: scale(1.04);
}

.testimonial-thumb iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.testimonial-thumb .play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  pointer-events: none;
  transition: background 0.2s;
}

.testimonial-thumb:hover .play-btn {
  background: var(--coral);
}

.testimonial-thumb .duration {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  pointer-events: none;
}

.testimonial-card h3 {
  margin: 14px 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.testimonial-card p {
  margin: 0;
  font-size: 15px;
  color: #666;
}

.final-cta {
  min-height: 420px;
  padding: 70px clamp(24px, 8vw, 130px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 50px;
  background: var(--teal);
}

.final-cta > div {
  max-width: 800px;
}

.final-cta span {
  font-size: 13px;
  font-weight: 850;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.final-cta h2 {
  margin: 12px 0 18px;
  font-size: clamp(42px, 5vw, 72px);
  letter-spacing: -0.055em;
  line-height: 0.98;
}

.final-cta p {
  max-width: 680px;
  margin: 0;
  font-size: 18px;
}

.final-button {
  min-width: 270px;
  background: var(--ink);
  color: #fff !important;
}

.final-button:hover {
  background: var(--coral);
  color: var(--ink) !important;
}

.site-footer {
  padding: 68px clamp(24px, 7vw, 110px) 25px;
  background: linear-gradient(160deg, #0d3b36 0%, #0a2b28 100%);
  color: #fff;
}

.footer-main {
  display: grid;
  grid-template-columns: 1.5fr repeat(3, 1fr);
  gap: 50px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.footer-logo img {
  width: 44px;
  height: 44px;
}

.footer-logo span {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.03em;
}

.footer-brand p {
  max-width: 330px;
  margin: 22px 0 0;
  color: #a7c6c0;
  line-height: 1.55;
}

.footer-contact {
  display: inline-block;
  margin-top: 22px;
  padding: 12px 20px;
  background: var(--coral);
  color: #fff !important;
  border-radius: 10px;
  font-weight: 750;
}

.footer-contact:hover {
  filter: brightness(1.06);
}

.footer-social {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 22px;
}

.footer-social a {
  color: #5fe3d0 !important;
  font-weight: 600;
}

.footer-main h3 {
  margin: 0 0 22px;
  font-size: 15px;
}

.footer-main > div:not(.footer-brand) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 13px;
}

.footer-main a {
  color: #a7c6c0;
  font-size: 14px;
  transition: color 0.18s ease;
}

.footer-main a:hover {
  color: #5fe3d0;
}

.footer-bottom {
  margin-top: 55px;
  padding-top: 22px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: #7fa39d;
  font-size: 13px;
}

@media (max-width: 1100px) {
  .desktop-nav,
  .header-actions {
    display: none;
  }

  .menu-toggle {
    width: 42px;
    height: 42px;
    margin-left: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 6px;
    background: transparent;
    border: 0;
  }

  .menu-toggle span {
    height: 2px;
    background: var(--ink);
  }

  .mobile-nav {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    padding: 26px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    background: var(--cream);
    border-bottom: 1px solid var(--ink);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.12);
    font-weight: 750;
  }

  .mobile-cta {
    text-align: center;
  }

  .hero {
    grid-template-columns: 1fr;
  }

  .hero-art {
    min-height: 520px;
  }

  .videos-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .vision-copy {
    padding: 60px;
  }
}

@media (max-width: 700px) {
  .announcement {
    padding: 10px 18px;
    flex-direction: column;
    gap: 2px;
    text-align: center;
  }

  .contact-strip {
    padding: 10px 16px;
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
  }

  .site-header {
    min-height: 76px;
    padding: 8px 18px;
  }

  .brand {
    width: 145px;
    height: 52px;
  }

  .hero {
    min-height: auto;
    padding: 70px 20px 75px;
    display: block;
    text-align: center;
  }

  .hero h1 {
    font-size: clamp(43px, 12vw, 64px);
    line-height: 0.96;
  }

  .hero-copy > p {
    margin-top: 25px;
    font-size: 17px;
  }

  .hero-actions {
    flex-direction: column;
  }

  .button-primary,
  .button-outline {
    width: 100%;
    min-width: 0;
  }

  .availability {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    text-align: left;
  }

  .hero-art {
    min-height: 380px;
    margin: 55px 4px 0;
  }

  .floating-card {
    min-width: 128px;
    padding: 10px 12px;
    text-align: left;
  }

  .floating-card strong {
    font-size: 14.5px;
  }

  .floating-card small {
    font-size: 10.5px;
  }

  .card-student {
    top: 3%;
    left: -2%;
  }

  .card-teacher {
    bottom: 3%;
    left: -2%;
  }

  .card-school {
    right: -2%;
    bottom: 2%;
  }

  .yt-section {
    padding: 65px 18px 75px;
  }

  .videos-grid {
    grid-template-columns: 1fr;
  }

  .section-heading h2 {
    font-size: 44px;
  }

  .vision {
    grid-template-columns: 1fr;
  }

  .vision-photo {
    min-height: 0;
  }

  .vision-copy {
    padding: 65px 22px;
  }

  .vision-copy blockquote {
    font-size: 30px;
  }

  .testimonials {
    padding: 70px 20px;
  }

  .testimonial-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .final-cta {
    padding: 65px 20px;
    align-items: flex-start;
    flex-direction: column;
  }

  .final-button {
    width: 100%;
  }

  .site-footer {
    padding: 55px 22px 24px;
  }

  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 38px 20px;
  }

  .footer-brand {
    grid-column: 1 / -1;
  }

  .footer-main > div:last-child {
    grid-column: 1 / -1;
  }

  .footer-bottom {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
